import React, { useState, useMemo } from "react";
import { Box, Typography, Button, Chip, Divider, Paper, DialogTitle, DialogContent, DialogActions, TextField, IconButton, Alert } from "@mui/material";
import {
  CheckCircleOutline,
  CalendarToday,
  Person,
  AccessTime,
  Visibility,
  Add,
  Close,
  Cancel,
} from "@mui/icons-material";
import { samplePMCals } from "./pmCalData";

import ResponsiveDialog from "../../Component/ResponsiveDialog";
// ─── Status & priority chip styles ───────────────────────────────────────────
const statusStyle = {
  "Pending Approval": { bgcolor: "#E65100", color: "#fff" },
  "In Progress": { bgcolor: "#2362B4", color: "#fff" },
  Approved: { bgcolor: "#2e7d32", color: "#fff" },
  Rejected: { bgcolor: "#C62828", color: "#fff" },
};
const priorityStyle = {
  High: { bgcolor: "#C62828", color: "#fff" },
  Medium: { bgcolor: "#757575", color: "#fff" },
  Low: { bgcolor: "#9e9e9e", color: "#fff" },
};

const mapStatus = (s) => {
  if (s === "pending") return "Pending Approval";
  if (s === "in-progress") return "In Progress";
  if (s === "completed") return "Approved";
  if (s === "rejected") return "Rejected";
  return s;
};

// ─── Component ────────────────────────────────────────────────────────────────
const PlannerApproval = () => {
  const [planners, setPlanners] = useState(() =>
    samplePMCals.map((p) => ({
      ...p,
      displayStatus: mapStatus(p.status),
      approvalComments: p.approvalComments || "",
      approvedBy: p.approvedBy || "",
      approvedAt: p.approvedAt || "",
      rejectedBy: p.rejectedBy || "",
      rejectedAt: p.rejectedAt || "",
    }))
  );

  // Approval dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPlanner, setSelectedPlanner] = useState(null);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [sigError, setSigError] = useState("");

  // Dynamic stats
  const stats = useMemo(() => {
    const total = planners.length;
    const inProgress = planners.filter(
      (p) => p.displayStatus === "In Progress"
    ).length;
    const pending = planners.filter(
      (p) => p.displayStatus === "Pending Approval"
    ).length;
    const rejected = planners.filter(
      (p) => p.displayStatus === "Rejected"
    ).length;
    return [
      { label: "Total Planners", value: total, color: "#1a2333" },
      { label: "In Progress", value: inProgress, color: "#2362B4" },
      { label: "Pending", value: pending, color: "#E65100" },
      { label: "Rejected", value: rejected, color: "#C62828" },
    ];
  }, [planners]);

  const openReviewDialog = (planner) => {
    setSelectedPlanner(planner);
    setUsername("");
    setPassword("");
    setReason("");
    setSigError("");
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedPlanner(null);
  };

  const handleApprove = () => {
    if (!username || !password) {
      setSigError("Please enter username and password for electronic signature");
      return;
    }
    setPlanners((prev) =>
      prev.map((p) =>
        p.id === selectedPlanner.id
          ? {
              ...p,
              displayStatus: "In Progress",
              status: "in-progress",
              approvedBy: username,
              approvedAt: new Date().toISOString(),
              approvalComments: reason,
            }
          : p
      )
    );
    closeDialog();
  };

  const handleReject = () => {
    if (!username || !password) {
      setSigError("Please enter username and password for electronic signature");
      return;
    }
    if (!reason) {
      setSigError("Please provide a reason for rejection");
      return;
    }
    setPlanners((prev) =>
      prev.map((p) =>
        p.id === selectedPlanner.id
          ? {
              ...p,
              displayStatus: "Rejected",
              status: "rejected",
              rejectedBy: username,
              rejectedAt: new Date().toISOString(),
              approvalComments: reason,
            }
          : p
      )
    );
    closeDialog();
  };

  const canReview = (pl) =>
    pl.displayStatus === "Pending Approval" || pl.status === "pending";

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f0f2f5" }}>
      {/* ── Page Header ── */}
      <Box
        sx={{
          bgcolor: "#fff",
          borderBottom: "1px solid #e0e4ea",
          px: 3.5,
          py: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
          <CheckCircleOutline sx={{ color: "#2362B4", fontSize: 30 }} />
          <Typography sx={{ fontSize: 22, fontWeight: 700, color: "#2362B4" }}>
            Planner Approval
          </Typography>
        </Box>
        <Typography
          sx={{ fontSize: 13, color: "#6b7280", mt: 0.3, ml: "40px" }}
        >
          Review and approve PM-Cal requests
        </Typography>
      </Box>

      <Box
        sx={{ p: 2.5, display: "flex", gap: 2.5, alignItems: "flex-start" }}
      >
        {/* ── Left: Planners List ── */}
        <Box
          sx={{
            flex: 1,
            bgcolor: "#fff",
            borderRadius: 2,
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            p: 2.5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2.5,
            }}
          >
            <Typography sx={{ fontWeight: 700, fontSize: 17, color: "#1a2333" }}>
              Yearly Planners
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() =>
                alert(
                  "Creating yearly planner PLAN-2026-7257 for 2026. This feature would typically generate a comprehensive calibration schedule for the entire year based on asset frequencies and requirements."
                )
              }
              sx={{
                bgcolor: "#2362B4",
                textTransform: "none",
                fontWeight: 600,
                borderRadius: 1.5,
                "&:hover": { bgcolor: "#1B4F96" },
              }}
            >
              Create Yearly Planner
            </Button>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {planners.map((pl) => (
              <Paper
                key={pl.id}
                elevation={0}
                sx={{ border: "1px solid #e0e4ea", borderRadius: 2, p: 2.5 }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      sx={{ fontWeight: 700, fontSize: 15, color: "#1a2333", mb: 0.5 }}
                    >
                      {pl.id} - {pl.assetName}
                    </Typography>
                    <Typography sx={{ fontSize: 13, color: "#6b7280", mb: 1.2 }}>
                      {pl.description}
                    </Typography>

                    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.4 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                        <CalendarToday sx={{ fontSize: 14, color: "#9ca3af" }} />
                        <Typography sx={{ fontSize: 13, color: "#4b5563" }}>
                          {pl.startDate} - {pl.dueDate}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                        <Person sx={{ fontSize: 14, color: "#9ca3af" }} />
                        <Typography sx={{ fontSize: 13, color: "#4b5563" }}>
                          Technician: {pl.technician || "Not assigned"}
                        </Typography>
                      </Box>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                        <AccessTime sx={{ fontSize: 14, color: "#9ca3af" }} />
                        <Typography sx={{ fontSize: 13, color: "#4b5563" }}>
                          Duration: {pl.estimatedDuration || "Not specified"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ mt: 1.5, display: "flex", gap: 1 }}>
                      <Chip
                        label={pl.displayStatus}
                        size="small"
                        sx={{
                          ...(statusStyle[pl.displayStatus] || {
                            bgcolor: "#757575",
                            color: "#fff",
                          }),
                          fontWeight: 600,
                          fontSize: 12,
                          height: 26,
                        }}
                      />
                      {pl.priority && (
                        <Chip
                          label={`${pl.priority} Priority`}
                          size="small"
                          sx={{
                            ...(priorityStyle[pl.priority] || {
                              bgcolor: "#757575",
                              color: "#fff",
                            }),
                            fontWeight: 600,
                            fontSize: 12,
                            height: 26,
                          }}
                        />
                      )}
                    </Box>
                  </Box>

                  <Box sx={{ ml: 2, mt: 0.5 }}>
                    {canReview(pl) ? (
                      <Button
                        variant="contained"
                        startIcon={<CheckCircleOutline />}
                        onClick={() => openReviewDialog(pl)}
                        sx={{
                          bgcolor: "#2362B4",
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 1.5,
                          "&:hover": { bgcolor: "#1B4F96" },
                        }}
                      >
                        Review
                      </Button>
                    ) : (
                      <Button
                        variant="contained"
                        startIcon={<Visibility />}
                        onClick={() => openReviewDialog(pl)}
                        sx={{
                          bgcolor: "#616161",
                          textTransform: "none",
                          fontWeight: 600,
                          borderRadius: 1.5,
                          "&:hover": { bgcolor: "#424242" },
                        }}
                      >
                        View Details
                      </Button>
                    )}
                  </Box>
                </Box>

                {pl.approvalComments && (
                  <Box sx={{ mt: 1.5, p: 1.5, bgcolor: "#f8f9fb", borderRadius: 1 }}>
                    <Typography sx={{ fontSize: 13, color: "#4b5563" }}>
                      <strong>Comments:</strong> {pl.approvalComments}
                    </Typography>
                    {pl.approvedBy && (
                      <Typography sx={{ fontSize: 12, color: "#9ca3af", mt: 0.3 }}>
                        Approved by {pl.approvedBy} on{" "}
                        {new Date(pl.approvedAt).toLocaleString()}
                      </Typography>
                    )}
                    {pl.rejectedBy && (
                      <Typography sx={{ fontSize: 12, color: "#9ca3af", mt: 0.3 }}>
                        Rejected by {pl.rejectedBy} on{" "}
                        {new Date(pl.rejectedAt).toLocaleString()}
                      </Typography>
                    )}
                  </Box>
                )}
              </Paper>
            ))}
          </Box>
        </Box>

        {/* ── Right: Stats Panel ── */}
        <Box
          sx={{
            width: 240,
            bgcolor: "#fff",
            borderRadius: 2,
            boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
            p: 2.5,
            flexShrink: 0,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#2362B4", mb: 2 }}>
            Approval Statistics
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {stats.map((s, i) => (
            <Box key={s.label} sx={{ mb: i < stats.length - 1 ? 2.5 : 0 }}>
              <Typography sx={{ fontSize: 13, color: "#6b7280", mb: 0.2 }}>
                {s.label}
              </Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 700, color: s.color }}>
                {s.value}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* ── Approval Dialog ── */}
      <ResponsiveDialog open={dialogOpen} onClose={closeDialog} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            bgcolor: "#2362B4",
            color: "#fff",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 1.5,
          }}
        >
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
            {selectedPlanner && canReview(selectedPlanner)
              ? "Planner Approval"
              : "Planner Details"}
          </Typography>
          <IconButton onClick={closeDialog} sx={{ color: "#fff" }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedPlanner && (
            <>
              <Typography
                sx={{ fontWeight: 700, fontSize: 15, color: "#1a2333", mb: 2, mt: 1 }}
              >
                PM-Cal Approval Request
              </Typography>
              <Box sx={{ display: "flex", gap: 4, mb: 3 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 13, mb: 0.8 }}>
                    <strong>PM-Cal ID:</strong> {selectedPlanner.id}
                  </Typography>
                  <Typography sx={{ fontSize: 13, mb: 0.8 }}>
                    <strong>Asset:</strong> {selectedPlanner.assetName}
                  </Typography>
                  <Typography sx={{ fontSize: 13, mb: 0.8 }}>
                    <strong>Tool:</strong> {selectedPlanner.toolName || "N/A"}
                  </Typography>
                  <Typography sx={{ fontSize: 13, mb: 0.8 }}>
                    <strong>Frequency:</strong>{" "}
                    {selectedPlanner.frequency || "N/A"}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: 13, mb: 0.8 }}>
                    <strong>Start Date:</strong> {selectedPlanner.startDate}
                  </Typography>
                  <Typography sx={{ fontSize: 13, mb: 0.8 }}>
                    <strong>Due Date:</strong> {selectedPlanner.dueDate}
                  </Typography>
                  <Typography sx={{ fontSize: 13, mb: 0.8 }}>
                    <strong>Priority:</strong> {selectedPlanner.priority || "N/A"}
                  </Typography>
                  <Typography sx={{ fontSize: 13, mb: 0.8 }}>
                    <strong>Technician:</strong>{" "}
                    {selectedPlanner.technician || "Not assigned"}
                  </Typography>
                </Box>
              </Box>

              <Typography sx={{ fontSize: 13, mb: 2 }}>
                <strong>Description:</strong> {selectedPlanner.description || "—"}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {canReview(selectedPlanner) && (
                <>
                  <Typography
                    sx={{ fontWeight: 700, fontSize: 14, color: "#1a2333", mb: 1.5 }}
                  >
                    Electronic Signature (FDA 21 CFR Part 11)
                  </Typography>

                  {sigError && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                      {sigError}
                    </Alert>
                  )}

                  <Box sx={{ display: "flex", gap: 2, mb: 2 }}>
                    <TextField
                      label="Username *"
                      size="small"
                      fullWidth
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                    />
                    <TextField
                      label="Password *"
                      type="password"
                      size="small"
                      fullWidth
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </Box>

                  <TextField
                    label="Reason for Action"
                    multiline
                    rows={2}
                    fullWidth
                    size="small"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Enter reason for approval/rejection"
                  />
                </>
              )}

              {!canReview(selectedPlanner) && selectedPlanner.approvalComments && (
                <Box sx={{ p: 2, bgcolor: "#f8f9fb", borderRadius: 1, mt: 1 }}>
                  <Typography sx={{ fontSize: 13 }}>
                    <strong>Comments:</strong> {selectedPlanner.approvalComments}
                  </Typography>
                  {selectedPlanner.approvedBy && (
                    <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>
                      Approved by {selectedPlanner.approvedBy} on{" "}
                      {new Date(selectedPlanner.approvedAt).toLocaleString()}
                    </Typography>
                  )}
                  {selectedPlanner.rejectedBy && (
                    <Typography sx={{ fontSize: 12, color: "#6b7280", mt: 0.5 }}>
                      Rejected by {selectedPlanner.rejectedBy} on{" "}
                      {new Date(selectedPlanner.rejectedAt).toLocaleString()}
                    </Typography>
                  )}
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {selectedPlanner && canReview(selectedPlanner) ? (
            <>
              <Button
                variant="contained"
                startIcon={<CheckCircleOutline />}
                onClick={handleApprove}
                sx={{
                  bgcolor: "#2e7d32",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#1b5e20" },
                }}
              >
                Approve
              </Button>
              <Button
                variant="contained"
                startIcon={<Cancel />}
                onClick={handleReject}
                sx={{
                  bgcolor: "#C62828",
                  textTransform: "none",
                  fontWeight: 600,
                  "&:hover": { bgcolor: "#b71c1c" },
                }}
              >
                Reject
              </Button>
              <Button onClick={closeDialog} sx={{ textTransform: "none" }}>
                Cancel
              </Button>
            </>
          ) : (
            <Button onClick={closeDialog} sx={{ textTransform: "none" }}>
              Close
            </Button>
          )}
        </DialogActions>
      </ResponsiveDialog>
    </Box>
  );
};

export default PlannerApproval;
